<script>
import { arrowDownIcon } from "@/utils/proposalIcons";
import { colorList } from "@/utils/staticData";

export default {
  name: "ColorPicker",
  props: {
    modelValue: { type: String, default: "#000000" },
    disabled: { type: Boolean, default: false },
    className: { type: String, default: "" },
    showPresets: { type: Boolean, default: true },
    showTransparent: { type: Boolean, default: true },
    presetColors: {
      type: Array,
      default: () => colorList
    },
  },
  emits: ["change"],
  data() {
    return {
      isOpen: false,
      error: false,
      currentColor: this.modelValue,
      pickerId: Math.floor(Math.random() * 1000),
      alignRight: false,
      dropDirection: 'down',
      arrowDownIcon
    };
  },
  watch: {
    modelValue(val) {
      this.currentColor = val;
    }
  },
  methods: {
    handlePresetClick(color) {
      this.currentColor = color;
      this.$emit("change", color);
    },
    handleColorChange(e) {
      const value = e.target.value;
      this.currentColor = value;
      this.$emit("change", value);
    },
    handleColorInputChange(e) {
      const value = e.target.value;
      if (/^#[0-9A-Fa-f]{6}$/.test(value)) {
        this.handlePresetClick(value);
        this.error = false;
      } else {
        setTimeout(() => {
          this.currentColor = this.modelValue;
          this.error = false;
        }, 1000);
        this.error = true;
      }
    },
    handleOutside(e) {
      if (!e.target.closest(`.picker-${this.pickerId}`)) {
        this.isOpen = false;
      }
    },
    async toggleDropdown() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        await this.$nextTick();
        const triggerRef = this.$refs.triggerRef;
        if (triggerRef) {
          const rect = triggerRef.getBoundingClientRect();
          let containerRight = window.innerWidth;
          let containerBottom = window.innerHeight;
          let containerTop = 0;

          // Find closest modal body or scroll container
          const container = triggerRef.closest('.modal-body') || triggerRef.closest('.manage-modal-body');
          if (container) {
            const containerRect = container.getBoundingClientRect();
            containerRight = containerRect.right - parseFloat(window.getComputedStyle(container).paddingRight || '0');
            containerBottom = containerRect.bottom;
            containerTop = containerRect.top;
          }

          // Check horizontal overflow
          if (containerRight - rect.left < 250) {
            this.alignRight = true;
          } else {
            this.alignRight = false;
          }

          // Check vertical overflow
          if (containerBottom - rect.bottom < 260 && rect.top - containerTop > 260) {
            this.dropDirection = 'up';
          } else {
            this.dropDirection = 'down';
          }
        }
      }
    }
  },
  mounted() {
    document.addEventListener("mousedown", this.handleOutside);
  },
  beforeDestroy() {
    document.removeEventListener("mousedown", this.handleOutside);
  }
};
</script>

<template>
  <div :class="[
    `picker-${pickerId}`,
    'color-picker',
    className,
    { disabled, 'align-right': alignRight, 'drop-up': dropDirection === 'up', 'is-open': isOpen },
  ]" ref="triggerRef">
    <!-- Trigger -->
    <div class="color-picker-trigger" @click="toggleDropdown">
      <input type="color" class="selected-pallet" v-model="currentColor" disabled />

      <span class="color-name">
        {{
          ((presetColors.find((c) => c.code === currentColor) || {}).name) ||
          currentColor
        }}
      </span>

      <span v-html="arrowDownIcon" class="arrow-icon" :class="{ open: isOpen }" />
    </div>

    <!-- Dropdown -->
    <div v-if="isOpen" class="color-picker-dropdown">
      <!-- Native Input -->
      <div class="color-picker-input-section">
        <div>
          <input id="nativeColorInput" class="picker-input" type="color" v-model="currentColor"
            @input="handleColorChange" />
        </div>

        <div class="color-input">
          <input type="text" class="hex-input" v-model="currentColor" placeholder="#000000" @input="
            handleColorInputChange
          " />
          <span v-if="error" class="hex-error">Invalid color code</span>
        </div>
      </div>

      <!-- Presets -->
      <div v-if="showPresets" class="color-picker-presets">
        <div v-for="(clr, i) in presetColors" :key="i" class="color-pallet"
          :class="{ selected: currentColor === clr.code }" :style="{ backgroundColor: clr.code }" :title="clr.name"
          @click="handlePresetClick(clr.code)" />
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.color-picker {
  position: relative;
  display: inline-block;
  width: 100%;

  &.is-open {
    z-index: 1000;
  }

  &.disabled {
    opacity: 0.5;
    pointer-events: none;
  }

  &.align-right &-dropdown {
    left: auto;
    right: 0;
  }

  &.drop-up &-dropdown {
    top: auto;
    bottom: 110%;
  }

  &-trigger {
    display: flex;
    align-items: center;
    gap: 8px;
    border1: 1px solid #ddd;
	border: none;
    padding: 8px 10px;
    border-radius: 6px;
    background: #fff;

    .color-name {
      display: inline-block;
      font-size: 14px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .selected-pallet {
      width: 14px;
      height: 14px;
      padding: 0px !important;
      margin: 0px !important;
      background: transparent;
      border-radius: 2px;
      border1: 1px solid #c9c2c2;
	  border: none;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;

      &::-webkit-color-swatch-wrapper {
        padding: 0;
      }

      &::-webkit-color-swatch {
        border: none;
      }
    }

    .arrow-icon {
      margin-left: auto;
      transition: 0.2s;
      color: #999;

      &.open {
        transform: rotate(180deg);
      }
    }
  }

  &-dropdown {
    position: absolute;
    top: 110%;
    left: 0;
    z-index: 10;
    background: #fff;
    border1: 1px solid #ddd;
	border: none;
    padding: 12px;
    border-radius: 6px;
    width: 100%;
    min-width: 230px;
  }

  &-input-section {
    display: inline-flex;
    gap: 8px;

    .picker-input {
      width: 28px;
      height: 28px;
      padding: 0px;
      background: transparent;
      border-radius: 4px;
      border1: 2px solid #ddd;
	  border: none;
      cursor: pointer;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;

      &::-webkit-color-swatch-wrapper {
        padding: 0px;
      }

      &::-webkit-color-swatch {
        border: none;
      }
    }

    .color-input {
      position: relative;

      .hex-input {
        border1: 1px solid #ddd;
		border: none;
        padding: 4px 10px;
        font-size: 12px;
        border-radius: 4px;
        width: 100%;
        color: v-bind('error ? "red" : "#333"');
      }

      .hex-error {
        position: absolute;
        bottom: -8px;
        left: 0px;
        color: red;
        font-size: 8px;
      }
    }
  }


  &-presets {
    margin-top: 12px;
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 6px;

    .color-pallet {
      width: 28px;
      height: 28px;
      border-radius: 4px;
      cursor: pointer;
      border: 2px solid rgb(201, 194, 194);

      &.selected {
        border-color: #333;
      }
    }
  }
}
</style>